/**
 * Axios 인스턴스 설정 및 인터셉터
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, clearAuthData } from './storage';

// API Base URL (환경 변수 또는 기본값)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1';

/**
 * Axios 인스턴스 생성
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30초
});

/**
 * 요청 인터셉터
 * - Authorization 헤더에 토큰 자동 추가
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * 응답 인터셉터
 * - 401 에러 시 자동 로그아웃 처리
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // 401 Unauthorized - 인증 실패
    if (error.response?.status === 401) {
      // 로그인 페이지가 아닌 경우에만 처리
      if (!window.location.pathname.includes('/login')) {
        console.error('Authentication failed. Redirecting to login...');
        clearAuthData();
        window.location.href = '/login';
      }
    }

    // 403 Forbidden - 권한 없음
    if (error.response?.status === 403) {
      console.error('Access forbidden');
    }

    // 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

/**
 * API 에러 메시지 추출
 */
export const getErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    // FastAPI 에러 형식
    if (typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    // Validation 에러 배열
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail
        .map((err: any) => err.msg || err.message)
        .join(', ');
    }
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.message) {
    return error.message;
  }

  return '알 수 없는 오류가 발생했습니다.';
};

export default api;
